import React, { useState, useEffect, useCallback } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, FormControlLabel, FormGroup } from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";
import { useFlags } from "launchdarkly-react-client-sdk";

import { analytics } from "services/analytics";
import Layout from "components/Screens/LoginV2/Layout/Layout";
import ToggleSwitch from "components/common/ToggleSwitch";
import Text from "components/common/Text";
import Button from "components/common/Button";
import PageLoader from "components/common/PageLoader";
import MicrosoftSignup from "components/Screens/Login/SignUp/MicrosoftSignup";
import GoogleSignup from "components/Screens/Login/SignUp/GoogleSignup";
import Auth0Login from "components/Screens/Login/SignUp/Auth0Login/Auth0Login";
import TermsAndPolicy from "components/common/TermAndPolicy/Terms-And-Policy";
import CustomAlert from "components/common/Alert";
import { AlertType } from "components/common/Snackbar";
import TextCheckBox from "components/common/TextCheckBox";

import { AuthUser, ResetAuthUser, AuthUserSuccess } from "store/actions/AuthUser";
import { UserAdditionalInfo } from "store/actions/UserAdditionalInfo";
import { GetPlatformConfig } from "store/actions/PlatformConfig";
import { showSnackbar } from "store/actions/Utility";

import { getAuthUserData, getAuthUserLodaing, getGoogleSignupInfo, getGoogleSignupLoading } from "selectors/auth";
import { getLoadingAdditionalInfo, getUserAdditionalInfo } from "selectors/user";
import { getPlatformConfig, getPlatformConfigLoading } from "selectors/platformConfig";

import { baseUrl } from "util/APIBaseUrl";
import loginWithAuth0 from "services/auth0";
import {
  getAuthenticationError,
  getUserInfoRequest,
  handlePostLoginNavigation,
} from "components/Screens/LoginV2/Login/utils";
import { getStorageItem } from "util/utils";
import { getLayoutImageProps } from "components/Screens/LoginV2/Layout/utils";

import { InfoOutlined } from "@mui/icons-material";

import { USER_TYPES } from "constants/index";
import { VALIDATE_TOKEN_MSGS } from "components/Screens/CustomerOnboarding/constants";
import {
  CUSTOMER_TAB,
  EMPLOYEE_TAB,
  FORM_DEFAULT_VALUES,
  LOGIN_TABS,
} from "components/Screens/LoginV2/Login/constants";
import { FORGOT_PASSWORD_ROUTE } from "constants/routes";

import "./Login.scss";

const {
  title: DEFAULT_TAB_TITLE,
  apiRequestPortal: CUSTOMER_API_REQUEST_PORTAL,
  forgotPasswordApiParam: CUSTOMER_FORGOT_PASSWORD_API_PARAM,
} = CUSTOMER_TAB;
const { apiRequestPortal: EMPLOYEE_API_REQUEST_PORTAL, forgotPasswordApiParam: EMPLOYEE_FORGOT_PASSWORD_API_PARAM } =
  EMPLOYEE_TAB;

function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { search, state: locationState, pathname } = useLocation();
  const { isAuthenticated: isAuth0Authenticated, getAccessTokenSilently } = useAuth0();
  const { replaceGoogleWithAuth0 = false, integratedContractSigning } = useFlags();

  const methods = useForm({
    defaultValues: FORM_DEFAULT_VALUES,
    mode: "onBlur",
  });
  const { handleSubmit, watch } = methods;
  const email = watch("email");
  const password = watch("password");

  const [currentTab, setCurrentTab] = useState(DEFAULT_TAB_TITLE);
  const [loginButtonClicked, setLoginButtonClicked] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isAuth0Loading, setIsAuth0Loading] = useState(false);
  const [onboardingError, setOnboardingError] = useState(null);

  const authUserData = useSelector(getAuthUserData);
  const isLoadingAuthUser = useSelector(getAuthUserLodaing);
  const userAdditionalInfo = useSelector(getUserAdditionalInfo);
  const isLoadingAdditionalInfo = useSelector(getLoadingAdditionalInfo);
  const platformConfig = useSelector(getPlatformConfig);
  const isLoadingPlatformConfig = useSelector(getPlatformConfigLoading);
  const googleSignupInfo = useSelector(getGoogleSignupInfo);
  const isLoadingGoogleSignup = useSelector(getGoogleSignupLoading);

  const { payload: platformPayload = {}, ok: isPlatformConfigLoaded = false } = platformConfig || {};
  const { MICROSOFT_LOGIN_ENABLED = "0", ENVIRONMENT } = platformPayload;
  const isCustomerTab = currentTab === DEFAULT_TAB_TITLE;
  const isProduction = ENVIRONMENT === "production";
  const isEmployeeTab = currentTab === EMPLOYEE_TAB.title;
  const showLoginForm = (isEmployeeTab && !isProduction) || isCustomerTab;
  const { userType: userTypeFromLocationState } = locationState ?? {};

  const layoutImageProps = getLayoutImageProps(isCustomerTab ? USER_TYPES.CUSTOMER : USER_TYPES.EMPLOYEE);

  const isLoading = isAuth0Loading || isLoadingPlatformConfig || isLoadingGoogleSignup || !isPlatformConfigLoaded;
  const shouldShowError =
    authUserData?.ok === false || googleSignupInfo?.ok === false || (loginButtonClicked && (!email || !password));

  const handleAuth0Login = useCallback(async () => {
    setIsAuth0Loading(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await loginWithAuth0(token);
      dispatch(AuthUserSuccess(response));
    } catch (error) {
      dispatch(
        showSnackbar({
          type: AlertType.error,
          message: error.message || "Something went wrong",
          dispatch,
        })
      );
    } finally {
      setIsAuth0Loading(false);
    }
  }, [getAccessTokenSilently, dispatch]);

  const handleUrlParameters = useCallback(() => {
    const searchParams = new URLSearchParams(search);
    const code = searchParams.get("code");

    if (!code) return;

    if (searchParams.get("state")) {
      // Employee redirected back from Auth0
      setIsAuth0Loading(true);
      if (isAuth0Authenticated) {
        handleAuth0Login();
      }
    } else {
      // Employee redirected back from Payoneer
      localStorage.setItem("payoneerCode", code);
      navigate("/main/dashboard");
    }
  }, [search, isAuth0Authenticated, handleAuth0Login, navigate]);

  const handleFormSubmit = useCallback(
    (data) => {
      setLoginButtonClicked(true);
      dispatch(ResetAuthUser({}));
      const { email: emailData, password: passwordData } = data;
      if (!emailData || !passwordData) return;

      const loginPortal = isCustomerTab ? CUSTOMER_API_REQUEST_PORTAL : EMPLOYEE_TAB.apiRequestPortal;

      dispatch(
        AuthUser({
          userCredential: {
            email: emailData,
            password: passwordData,
            loginPortal,
          },
          request: `${baseUrl}/auth/${loginPortal}`,
          loginPortal,
        })
      );
    },
    [currentTab, dispatch]
  );

  const getErrorMessage = () => {
    if (authUserData?.ok === false) {
      return getAuthenticationError(authUserData?.payload?.error);
    }
    if (googleSignupInfo?.ok === false) {
      return getAuthenticationError(googleSignupInfo?.payload?.error);
    }
    return "Email address not found";
  };

  const handleForgotPassword = () => {
    const userType = isCustomerTab ? CUSTOMER_FORGOT_PASSWORD_API_PARAM : EMPLOYEE_FORGOT_PASSWORD_API_PARAM;
    navigate(`${FORGOT_PASSWORD_ROUTE}?userType=${userType}`);
  };

  useEffect(() => {
    dispatch(GetPlatformConfig({}));
  }, [dispatch]);

  // PRD-2165: the Enhanced Portal handoff used to live here, but it raced the onboarding
  // navigation below — whichever response landed first won, and a customer mid-onboarding
  // was routed away before the redirect could run. It now lives in app.tsx, which no
  // navigation unmounts.

  useEffect(() => {
    if (userTypeFromLocationState) {
      setCurrentTab(userTypeFromLocationState);
    }
  }, [userTypeFromLocationState]);

  useEffect(() => {
    const loggedInUser = getStorageItem({ key: "loggedInUser", parse: true });
    const accessToken = getStorageItem({ key: "accessToken" });

    if (accessToken && loggedInUser) {
      const request = getUserInfoRequest(loggedInUser);
      if (request) {
        dispatch(UserAdditionalInfo(request));
      }
    }

    if (Object.keys(authUserData || {}).length > 0 && authUserData.ok !== true) {
      setLoginButtonClicked(true);
    }
  }, [authUserData, dispatch]);

  useEffect(() => {
    const loggedInUser = getStorageItem({ key: "loggedInUser", parse: true });
    const accessToken = getStorageItem({ key: "accessToken" });

    if (userAdditionalInfo && Object.keys(userAdditionalInfo).length && accessToken && loggedInUser) {
      // Analytics tracking
      analytics.track("Logged In", {
        channel: "password",
        name: loggedInUser?.name,
        email: loggedInUser?.email,
        userID: loggedInUser?.userId,
        accountType: loggedInUser?.type,
      });

      handlePostLoginNavigation(loggedInUser, userAdditionalInfo, navigate, integratedContractSigning);
    }

    if (userAdditionalInfo && Object.keys(userAdditionalInfo).length > 0 && userAdditionalInfo.ok !== true) {
      setLoginButtonClicked(true);
    }
  }, [userAdditionalInfo, navigate]);

  useEffect(() => {
    handleUrlParameters();
  }, [isAuth0Authenticated, handleUrlParameters]);

  useEffect(() => {
    if (locationState?.onboardingError) {
      setCurrentTab(DEFAULT_TAB_TITLE);
      setOnboardingError(locationState.onboardingError);
      window.history.replaceState({}, "", pathname);
    }
  }, []);

  if (isLoading) {
    return (
      <Layout {...layoutImageProps}>
        <div className="login-form-container">
          <div className="login-header">
            <PageLoader />
          </div>
        </div>
      </Layout>
    );
  }

  const renderSocialLogin = () => {
    if (!isPlatformConfigLoaded) return null;

    if (isCustomerTab) {
      return (
        <div className="social-login">
          <GoogleSignup loginPortal={CUSTOMER_API_REQUEST_PORTAL} buttonText="Google" />
          {MICROSOFT_LOGIN_ENABLED === "1" && (
            <MicrosoftSignup loginPortal={CUSTOMER_API_REQUEST_PORTAL} buttonText="Microsoft" />
          )}
        </div>
      );
    }

    return (
      <div className="social-login">
        {replaceGoogleWithAuth0 ? (
          <div className="flex flex-col ">
            <Auth0Login />
            <div className="edge-id-text-bg">
              <span className="para-body-s-regular color-text-body-secondary">
                Access your account with Edge ID, a secure authentication system that connects you to all Edge services.
              </span>
            </div>
          </div>
        ) : (
          <GoogleSignup loginPortal={EMPLOYEE_API_REQUEST_PORTAL} buttonText="Google" />
        )}
      </div>
    );
  };

  const getContentContainerClass = () => {
    if (isCustomerTab) {
      return "login-content-container";
    }
    if (isEmployeeTab) {
      return "login-content-container-employee";
    }
    return "login-content-container";
  };

  return (
    <Layout contentContainerClass={getContentContainerClass()} {...layoutImageProps}>
      <div className="login-form-container">
        <div className="login-header">
          <div className="heading-h1-semibold login-text">Log in to Edge</div>
          <div className="para-body-m-regular color-text-body-primary">Enter your details to continue</div>
        </div>

        <div className="login-toggle">
          <ToggleSwitch tabs={LOGIN_TABS} currentTab={currentTab} setCurrentTab={setCurrentTab} />
        </div>
        {integratedContractSigning && onboardingError && (
          <div className="mt-15 mb-15">
            <CustomAlert
              severity="info"
              variant="standard"
              description={`${VALIDATE_TOKEN_MSGS[onboardingError]}`}
              icon={<InfoOutlined className="color-text-highlight" />}
            />
          </div>
        )}

        {showLoginForm && (
          <>
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(handleFormSubmit)} className="login-form">
                <div className="form-field">
                  <Text name="email" label="Email address" type="email" fullWidth size="small" />
                </div>

                <div className="form-field">
                  <Text name="password" label="Password" type="password" fullWidth size="small" showPasswordEye />
                </div>

                {shouldShowError && (
                  <div className="form-field">
                    <Alert severity="error" className="login-alert">
                      {getErrorMessage()}
                    </Alert>
                  </div>
                )}

                <div className="form-options">
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <TextCheckBox
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          size="small"
                        />
                      }
                      label={<span className="heading-h5-regular color-text-body-secondary">Remember me</span>}
                    />
                  </FormGroup>

                  <Button
                    className="forgot-password-link para-body-s-medium"
                    variant="text"
                    onClick={handleForgotPassword}
                    label="Forgot password?"
                    disableHoverEffect
                  />
                </div>

                <div className="form-field">
                  <Button
                    type="submit"
                    label="Login"
                    variant="contained"
                    color="primary"
                    size="large"
                    isFullWidth
                    isDisabled={email.trim() === "" || password.trim() === ""}
                    isLoading={isLoadingAuthUser || isLoadingAdditionalInfo}
                  />
                </div>
              </form>
            </FormProvider>
            <div className="login-divider">
              <div className="para-body-s-regular color-text-body-secondary">
                {isEmployeeTab ? "or " : "or continue with"}
              </div>
            </div>
          </>
        )}

        {renderSocialLogin()}

        <TermsAndPolicy
          userType={isCustomerTab ? USER_TYPES.CUSTOMER : USER_TYPES.EMPLOYEE}
          className="subheader-text"
        />
      </div>
    </Layout>
  );
}

export default Login;
